import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';

import { AviionHttpClient } from '@core/http';
import { StorageService } from '@core/storage';
import { environment } from 'environments/environment';

import { ICampaignPlanBenefitDto, IPreauthEligibilityResponseDto } from './card-preauth-api.dto';
import { ICampaignBenefit, ICampaignPlanBenefit, ICardPreauthEligibility } from './card-preauth.models';

/**
 * Dev-only stand-in for the BE (testing every notice state): a JSON
 * eligibility answer in localStorage under this key replaces the request. Besides the
 * DTO fields it accepts `expiresInDays` (fractional) for an expiry relative to now, e.g.
 *   localStorage['aviion.mock.preauthEligibility'] =
 *     '{"showBanner":true,"eligible":true,"verificationAmount":0.1,"expiresInDays":3}'
 * A campaign offer carries one entry per plan — free days (dates required):
 *   '{"showBanner":false,"eligible":false,"verificationAmount":0.1,"campaignBenefitAvailable":true,
 *     "campaignBenefits":[{"planReferenceId":"<plan ref>","benefitType":"FREEDAYS","freeDays":7,"bonusDays":0,
 *     "freeFrom":"2026-11-01T00:00:00","freeTo":"2026-11-07T23:59:59","firstChargeAt":"2026-11-07T23:59:59"}]}'
 * or bonus days (no dates; charged at renewal with a live subscription, now without one):
 *   '{"showBanner":true,"eligible":true,"verificationAmount":0.1,"expiresInDays":27,"campaignBenefitAvailable":true,
 *     "campaignBenefits":[{"planReferenceId":"<plan ref>","benefitType":"BONUSDAYS","freeDays":0,"bonusDays":30,
 *     "bonusChargeMode":"AT_RENEWAL","freeFrom":null,"freeTo":null,"firstChargeAt":null}]}'
 * Ignored in production builds.
 */
export const MOCK_ELIGIBILITY_KEY = 'aviion.mock.preauthEligibility';

const DAY_MS = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class CardPreauthApiService {
    private readonly http = inject(AviionHttpClient);
    private readonly storage = inject(StorageService);

    public getEligibility(): Observable<ICardPreauthEligibility> {
        const mock = environment.production ? null : this.mockAnswer();
        const dto$ = mock ? of(mock) : this.http.get_2('api/v1/payments/preauth-eligibility');
        return dto$.pipe(map(dto => toEligibility(dto as IPreauthEligibilityResponseDto)));
    }

    private mockAnswer(): IPreauthEligibilityResponseDto | null {
        const mock = this.storage.getJson<IPreauthEligibilityResponseDto & { expiresInDays?: number }>(MOCK_ELIGIBILITY_KEY);
        if (!mock) return null;
        if (typeof mock.expiresInDays === 'number') {
            mock.subscriptionActiveTo = new Date(Date.now() + mock.expiresInDays * DAY_MS).toISOString();
        }
        return mock;
    }
}

function toEligibility(dto: IPreauthEligibilityResponseDto | null): ICardPreauthEligibility {
    const expiresAt = dto?.subscriptionActiveTo ? new Date(dto.subscriptionActiveTo) : null;
    return {
        showBanner: dto?.showBanner === true,
        eligible: dto?.eligible === true,
        verificationAmount: dto?.verificationAmount ?? 0,
        expiresAt: expiresAt && !isNaN(expiresAt.getTime()) ? expiresAt : null,
        cardPreregistered: dto?.cardPreregistered === true,
        campaignBenefit: toCampaignBenefit(dto),
    };
}

/** Parses an ISO date string; null for missing/unparseable input. */
function toDate(s?: string | null): Date | null {
    const d = s ? new Date(s) : null;
    return d && !isNaN(d.getTime()) ? d : null;
}

/**
 * Null-safe: never throws for an older BE payload without the campaign fields. Yields null
 * unless the BE says the benefit is available with at least one usable plan entry.
 */
function toCampaignBenefit(dto: IPreauthEligibilityResponseDto | null): ICampaignBenefit | null {
    if (dto?.campaignBenefitAvailable !== true) return null;
    // A plan may come twice when two campaigns cover it (one per type); one offer per plan is
    // shown and checked out — the one with more days, the first on a tie.
    const byPlan = new Map<string, ICampaignPlanBenefit>();
    for (const p of Array.isArray(dto.campaignBenefits) ? dto.campaignBenefits : []) {
        const offer = toPlanBenefit(p);
        const kept = offer && byPlan.get(offer.planReferenceId);
        if (offer && (!kept || offer.days > kept.days)) byPlan.set(offer.planReferenceId, offer);
    }
    const plans = [...byPlan.values()];
    if (plans.length === 0) return null;
    const headline = plans.reduce((best, p) => (p.days > best.days ? p : best));
    return { headline, plans };
}

/**
 * One plan's offer, or null when the entry is malformed — dropped rather than shown with made-up
 * copy. A free-days offer needs its days and all three dates; a bonus-days offer needs its days
 * (its `bonusChargeMode` is not read — see `IBonusDaysPlanBenefit`). A missing `benefitType` is a
 * BE predating bonus days: free days.
 */
function toPlanBenefit(p: ICampaignPlanBenefitDto | null): ICampaignPlanBenefit | null {
    if (typeof p?.planReferenceId !== 'string') return null;
    const type = p.benefitType ?? 'FREEDAYS';
    if (type === 'FREEDAYS') {
        const days = Number(p.freeDays);
        const [freeFrom, freeTo, firstChargeAt] = [toDate(p.freeFrom), toDate(p.freeTo), toDate(p.firstChargeAt)];
        if (!(days > 0) || !freeFrom || !freeTo || !firstChargeAt) return null;
        return { type, planReferenceId: p.planReferenceId, days, freeFrom, freeTo, firstChargeAt };
    }
    if (type === 'BONUSDAYS') {
        const days = Number(p.bonusDays);
        if (!(days > 0)) return null;
        return { type, planReferenceId: p.planReferenceId, days };
    }
    return null;
}
